import { createSlice } from '@reduxjs/toolkit';

import storage from '../../../../shared/storage';
import { GlobalState } from '../../../types.global';

import { clickedAd } from '../../tiles/slice';
import { isSameDay, timeInMs } from '../../../../shared';
import { INITIAL_STATE } from './shared';
import * as actions from './actions';
import { UserOnboarding, UserState, UserStateExperiment } from './types';
import { mergeInPersistent } from '../../../store/utils';
import { browserStorageUpdate } from '../../../store/actions';

// const GAINS = [20, 15, 10, 10, 7, 7, 5, 5, 4, 4, 3, 3, 2, 2, 1];

const parseOnboarding = (onboarding: UserOnboarding): UserOnboarding => {
  const parseValue = (value) => {
    if (value === null) return 5;
    if (value === true) return 0;
    return typeof value === 'number' ? value : 5;
  };
  return Object.keys(onboarding).reduce(
    (acc, key) => ({
      ...acc,
      [key]: parseValue(onboarding[key]),
    }),
    {
      startedAt: 0,
      shells: 0,
      counter: 0,
      topSites: 0,
      customizeBg: 0,
      shellsAlgorithm: 0,
      counterFirstBottle: 0,
      searchForm: 0,
      lastTopSitesViewed: 0,
      socialFeed: 0,
    }
  );
};

const getPointValue = (time) => {
  if (time <= 1000 * 2) return -10;
  if (time > 1000 * 60) return 1000;
  return 0;
};

const APP_TYPE = process.env.APP_TYPE;

const DEV_HARDCODED_FLAGS = {
  ADMARKETPLACE_FEED: 6,
  AD_TILES: 'adMarketplace',
  AD_AUTOSUGGESTION: 'adMarketplace',
  AD_AUTOSUGGESTION_BLACKLIST: [['term', 'PC Cleaner© (Download)']],
  SEARCH_BOX_AUTOFOCUS_DEFAULT: true,
  CUSTOM_SEARCH_ENGINE: true,
  SURVEY_CHEST: true,
  SHELL_TRIVIA_OFF: false,
  MAGIC_SEARCH_BOX: true,

  SEARCH_BOX_INSTANT_ANSWERS: false,

  SEARCH_BOX_SHORTCUT: true,
  SEARCH_BOX_FOCUS_HISTORY: true,
  SEARCH_TAGS: true,
  CUSTOMIZE_ALTERNATIVE_LOOK: true,
  THINKSUGGEST_ADS: false,
  UI_EXTRA_MODES: true,
  ZP_SUGGESTIONS: true,
  ZP_ANNOTATIONS: true,
  ZP_TILES: true,
  AD_WHALE: true,

  COLLECT_SHELLS_IN_GOOGLE_OFF: false,
  GOOGLE_TILES_ON: true,
  GOOGLE_SIDEBAR_ON: true,
  GOOGLE_COUNTER_ON: true,

  BANNER_ON: true,
  FIFTH_TILE: true,
  BOOKMARKS_ON: true,
  NEWS_ON: true,

  GAMES_NEW_TITLE: true,
};

// CUSTOM_SEARCH_ENGINE

const parseExperiment = (
  isDev: boolean,
  experiment: UserStateExperiment
): UserStateExperiment =>
  isDev
    ? {
      ...experiment,
      value: {
        scenario: {
          ...experiment.value.scenario,
          featureFlags: {
            ...experiment.value.scenario.featureFlags,
            ...DEV_HARDCODED_FLAGS,
            ...(typeof window === 'undefined'
              ? {}
              : storage.getItem('FEATURE_FLAGS') || {}),
          },
        },
      },
    }
    : experiment;

export const getInitialUserState = (persistedState: GlobalState): UserState => {
  const shells = {
    ...INITIAL_STATE.shells,
    ...(persistedState.user.shells || {}),
  };
  const searchCounter = persistedState.user.searchCounter || 0;
  const perBottle = APP_TYPE === 'lite' ? 100 : 20;

  const isDev = storage.getItem('isDev');

  const state = {
    ...mergeInPersistent(INITIAL_STATE, persistedState.user),
    isDev: !!isDev,
    experiment: parseExperiment(
      !!isDev,
      persistedState.user.experiment?.value
        ? persistedState.user.experiment
        : INITIAL_STATE.experiment
    ),

    shells: {
      ...mergeInPersistent(INITIAL_STATE.shells, persistedState.user.shells),
      shellsCounter: Math.max(
        shells.shellsCounter,
        searchCounter * perBottle +
        ((APP_TYPE === 'lite' && shells.counter) || 0)
      ),
    },
    onboarding: parseOnboarding({
      ...INITIAL_STATE.onboarding,
      ...(persistedState.user.onboarding || {}),
    }),
  };

  state.showBookmarksTooltip = state.showBookmarksTooltip
    ? false
    : state.showBookmarksTooltip;

  if (state.experiment.value.scenario.featureFlags.BOOKMARKS_ON) {
    state.showBookmarksTooltip = null;
  }

  const now = Date.now();

  state.quickReload = Math.min(
    state.quickReload + getPointValue(now - state.openedAt),
    100
  );

  state.isFastReload = now - (state.openedAt || 0) < 5000;
  state.isFirstTimeToday = !state.openedAt || !isSameDay(state.openedAt, now);
  if (state.isFirstTimeToday) {
    state.shells.trackingBufferPrev = state.shells.trackingBufferCurrent;
    state.shells.trackingBufferCurrent = 0;
    state.ntpDayVisitCounter = 0;

    const daysSinceLast = Math.floor((now - state.openedAt) / timeInMs.day);
    state.activityCounter = daysSinceLast < 7 ? state.activityCounter + 1 : 1;
  }

  state.previouslyOpenedAt = state.openedAt;
  state.openedAt = now;
  state.ntpDayVisitCounter = (state.ntpDayVisitCounter || 0) + 1;
  state.ntpVisitCounter++;

  if (
    !state.installationDate ||
    new Date(state.installationDate).toString() === 'Invalid Date'
  ) {
    state.installationDate = Date.now();
  }

  const lastOnboardingReset =
    state.onboarding?.resetAt || state.installationDate;

  if (lastOnboardingReset < now - timeInMs.day * 90) {
    state.onboarding.resetAt = now;
    state.onboarding.topSites = 5;
    state.onboarding.lastTopSitesViewed = now;
    // Show the popup after 7 days
  } else if (state.onboarding.lastTopSitesViewed < now - timeInMs.day * 7) {
    state.onboarding.topSites = 5;
    state.onboarding.lastTopSitesViewed = now;
  }

  return state;
};

export const userSlice = createSlice({
  name: 'user',
  initialState: null,
  reducers: {
    setTopSitesViewed: (state, { payload }) => {
      state.onboarding.lastTopSitesViewed = payload;
    },

    setIsShowFeed: (state, { payload }) => {
      state.isShowFeed = payload;
    },
    setViewedStories: (state, { payload }) => {
      state.viewedStories = payload;
    },
    gainedShells: (state, { payload }) => {
      state.shells.boosterUsed = false;
      if (payload.source === 'tabOpens') {
        state.shells.trackingBufferCurrent += payload.gain;
      }

      if (!isSameDay(state.shells.updatedAt, payload.updatedAt)) {
        state.shells.increment = 0;
        state.shells.boosters = 5;
        state.shells.dailyShellsCounter = 0;
      }

      state.isGainedBottles =
        Math.floor(state.shells.shellsCounter / 100) <
        Math.floor(payload.value / 100);
      state.shells.shellsCounter = payload.value;
      state.shells.updatedAt = payload.updatedAt;
    },
    setInstallSource: (state, { payload }) => {
      state.installSource = payload;
    },
    setUtmCampaign: (state, { payload }) => {
      state.utmCampaign = payload;
    },
    toggleBookmarksNotification: (state, { payload }) => {
      state.bookmarksNotificationHidden = payload;
    },
    setBookmarksTooltip: (state, { payload }) => {
      state.showBookmarksTooltip = payload;
      state.bookmarksDiscovered = true;
    },
    shownOnboardingStep: (state, { payload }) => {
      const value = state.onboarding[payload.step];
      state.onboarding[payload.step] = Math.max(value - 1, 0);
    },
    hideOnboardingStep: (state, { payload }) => {
      state.onboarding[payload.step] = 0;
    },
    trackedRetention: (state, { payload }) => {
      state.lastTrackRetention = payload.when;
    },
    fetchedExperiment: (state, { payload }) => {
      if (!payload.value?.scenario) return;

      state.experiment = parseExperiment(state.isDev, {
        value: {
          ...payload.value,
          scenario: {
            ...payload.value.scenario,
            featureFlags: {
              ...INITIAL_STATE.experiment.value.scenario.featureFlags,
              ...payload.value.scenario.featureFlags,
            },
          },
        },
        fetchedAt: payload.fetchedAt,
      });
    },
    updatedUser: (state, { payload }) => {
      state.loggedUser.value = {
        ...state.loggedUser.value,
        ...payload,
      };
    },
    createdUser: (state, { payload }) => {
      state.loggedUser.fetchedAt = 0;
      state.loggedUser.value = payload.user;
      state.loggedUser.token = payload.token;
      state.loggedUser.isExtension = true;
    },
    fetchedUserId: (state, { payload }) => {
      state.userId = payload;
    },
  },
  extraReducers: {
    [browserStorageUpdate.toString()]: (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialUserState(payload.state);
      }

      // TODO: instead of "data" should be "state". Check if it won't cause any problems after fixing
      if (payload.data?.user) {
        state.userId = payload.user.userId || state.userId;
        state.installationDate =
          payload.user.installationDate || state.installationDate;
      }
    },
    STORAGE_UPDATE: (state, { payload }) => ({
      ...payload.user,
      onboarding: Object.keys(state.onboarding).reduce(
        (acc, key) => ({
          ...acc,
          [key]: Math.min(state.onboarding[key], payload.user.onboarding[key]),
        }),
        {}
      ),
      isGainedBottles: false,
    }),
    [clickedAd.toString()]: (state) => {
      state.shells = {
        ...state.shells,
        boosters: state.shells.boosters - 1,
        boosterUsed: state.shells.boosterUsed || state.shells.boosters > 0,
      };
    },
    [actions.fetchLoggedUser.fulfilled.toString()]: (state, { payload }) => {
      if (!payload) return;
      state.loggedUser.fetchedAt = payload.fetchedAt;
      state.loggedUser.token = payload.token;

      if (payload.data) {
        state.loggedUser.value = payload.data.user;
        state.shells.shellsCounter = Math.max(
          state.shells.shellsCounter,
          payload.data.user.shellsCounter
        );
      }
    },
    [actions.fetchGoogleAccounts.fulfilled.toString()]: (
      state,
      { payload }
    ) => {
      state.googleAccounts = payload;
    },
    [actions.fetchGoogleAccounts.rejected.toString()]: (state) => {
      state.googleAccounts = null;
    },
  },
});

// -----------------------------------------------------------------------------
// ACTIONS

export const {
  setIsShowFeed,
  setViewedStories,
  toggleBookmarksNotification,
  setBookmarksTooltip,
  shownOnboardingStep,
  hideOnboardingStep,
  trackedRetention,
  updatedUser,
  createdUser,
  gainedShells,
  fetchedUserId,
  setInstallSource,
  setUtmCampaign,
  setTopSitesViewed,
} = userSlice.actions;

export default userSlice.reducer;
